/**
 * iter307e — Generic, type-aware sort comparator used by `<SortableTable>`
 * and its sibling utils (`sortInvoices`, `sortEnrollments`). One place
 * to get null-handling, date parsing, and numeric coercion right —
 * stops the recurring "sort silently broken" class of bug.
 *
 * Rules:
 *   - Null / undefined / '' ALWAYS sinks to the bottom regardless of
 *     direction. This is what humans actually want: a sparse column
 *     shouldn't bury its real values when sorted asc.
 *   - Dates parsed via Date(). NaN dates (malformed strings) treated
 *     as missing.
 *   - Numbers parsed via parseFloat. NaN treated as missing.
 *   - Strings compared via localeCompare (case-insensitive).
 *   - Accessor allows mapping UI column key → row field(s) (e.g.
 *     `parent` → `row.parent_name || row.parent_email`).
 *   - Returns a NEW array; never mutates input.
 *
 * @param {Array} rows
 * @param {Object} sort  { field: string|null, direction: 'asc'|'desc' }
 * @param {Array} columns  Optional config providing per-column `type`
 *                         and `accessor` hints. Same shape used by
 *                         `<SortableTable>`. If omitted, sort falls
 *                         back to string-compare.
 * @returns {Array}
 */
export function sortRows(rows, sort, columns) {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  const field = sort?.field;
  if (!field) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;

  const col = (columns || []).find((c) => c.key === field);
  const type = col?.type || 'string';
  const accessor = col?.accessor || ((r) => r[field]);

  const out = [...rows];
  out.sort((a, b) => {
    const rawA = accessor(a);
    const rawB = accessor(b);
    const cmp = compareCell(rawA, rawB, type);
    // Infinity sentinels signal a missing value — they MUST sink to
    // the bottom regardless of direction, so we don't multiply them
    // by `dir`. A naive `cmp * dir` would invert the sink in desc
    // (Infinity * -1 = -Infinity → row floats to the top).
    if (cmp === Number.POSITIVE_INFINITY) return 1;
    if (cmp === Number.NEGATIVE_INFINITY) return -1;
    return cmp * dir;
  });
  return out;
}

/**
 * Compares two cell values per the column's declared type. Returns
 * one of:
 *   - 0 (equal)
 *   - a finite number (signed; caller applies direction)
 *   - Number.POSITIVE_INFINITY  (A is missing → A sinks)
 *   - Number.NEGATIVE_INFINITY  (B is missing → B sinks)
 *
 * The caller (`sortRows`) must NOT multiply Infinity by the direction,
 * otherwise desc would flip the sink to the top. See the wrap in
 * `sortRows` below.
 */
export function compareCell(rawA, rawB, type = 'string') {
  const aHas = rawA != null && rawA !== '';
  const bHas = rawB != null && rawB !== '';
  if (!aHas && !bHas) return 0;
  if (!aHas) return Number.POSITIVE_INFINITY;
  if (!bHas) return Number.NEGATIVE_INFINITY;

  if (type === 'date') {
    const aTs = new Date(rawA).getTime();
    const bTs = new Date(rawB).getTime();
    const aBad = Number.isNaN(aTs);
    const bBad = Number.isNaN(bTs);
    if (aBad && bBad) return 0;
    if (aBad) return Number.POSITIVE_INFINITY;
    if (bBad) return Number.NEGATIVE_INFINITY;
    return aTs - bTs;
  }

  if (type === 'numeric' || type === 'number') {
    const aNum = parseFloat(rawA);
    const bNum = parseFloat(rawB);
    const aBad = Number.isNaN(aNum);
    const bBad = Number.isNaN(bNum);
    if (aBad && bBad) return 0;
    if (aBad) return Number.POSITIVE_INFINITY;
    if (bBad) return Number.NEGATIVE_INFINITY;
    return aNum - bNum;
  }

  // String fallback — case-insensitive locale compare
  return String(rawA).toLowerCase().localeCompare(String(rawB).toLowerCase());
}

/**
 * iter307e — Reducer for the cycle-sort click handler:
 *   1st click new column → asc
 *   2nd click same column → desc
 *   3rd click same column → CLEARED (no sort)
 *
 * The third-click clear was the missing piece in the old Transactions
 * page — its handler instead snapped back to (date, desc), which on
 * the date column itself looked like the click did nothing.
 *
 * Use:
 *   const next = nextSortState(prev, clickedColumn);
 */
export function nextSortState(prev, clickedField) {
  if (!clickedField) return prev;
  if (!prev || prev.field !== clickedField) {
    return { field: clickedField, direction: 'asc' };
  }
  if (prev.direction === 'asc') {
    return { field: clickedField, direction: 'desc' };
  }
  // 3rd click → fully clear
  return { field: null, direction: 'asc' };
}
